"use client"
import { useState, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { logout } from "../store/slices/authSlice"
import { fetchStudentById } from "../store/slices/studentSlice"
import { fetchFees } from "../store/slices/feeSlice"
import { fetchDocuments, viewDocument, downloadDocument } from "../store/slices/documentSlice"
import { fetchResults } from "../store/slices/resultSlice"
import AddDocument from "./AddDocument"
import StudentIDCard from "./StudentIDCard"
import StudentFeeReceipt from "./StudentFeeReceipt"
import {
  User,
  CreditCard,
  FileText,
  FolderOpen,
  LogOut,
  GraduationCap,
  IndianRupee,
  CheckCircle,
  Clock,
  MenuIcon,
  Eye,
  Download,
  Edit,
  Upload,
  Award as IdCard,
  ReceiptIcon,
  Printer,
} from "lucide-react"
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Button,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material"

// --- Color Palette ---
const PRIMARY_COLOR = "#3f51b5"
const PRIMARY_LIGHT = "#5c6bc0"
const ACCENT_COLOR = "#00c853"
const WARNING_COLOR = "#ff9800"
const DANGER_COLOR = "#e53935"
const BG_COLOR = "#f4f6f8"

const StudentDashboard = () => {
  const dispatch = useDispatch()
  const { user, loading: authLoading } = useSelector((state) => state.auth)
  const { currentStudent } = useSelector((state) => state.students)
  const { fees } = useSelector((state) => state.fees)
  const { documents } = useSelector((state) => state.documents)
  const { results, loading: resultsLoading, error: resultsError } = useSelector((state) => state.results)
  const [activeSection, setActiveSection] = useState(() => {
    return localStorage.getItem("studentActiveSection") || "profile"
  })
  const [mobileOpen, setMobileOpen] = useState(false)
  const [message, setMessage] = useState({ text: "", type: "" })
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingDoc, setEditingDoc] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [viewingFee, setViewingFee] = useState(null)
  const [showReceipt, setShowReceipt] = useState(false)
  const [receiptFee, setReceiptFee] = useState(null) // New state for receipt modal
  const receiptRef = useRef(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"))

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchStudentById(user.id))
      dispatch(fetchFees())
      dispatch(fetchDocuments())
      dispatch(fetchResults())
    }
  }, [dispatch, user])

  useEffect(() => {
    localStorage.setItem("studentActiveSection", activeSection)
  }, [activeSection])

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ text: "", type: "" }), 3000)
      return () => clearTimeout(timer)
    }
  }, [message])

  const handleLogout = () => {
    localStorage.removeItem("studentActiveSection")
    setActiveSection("profile")
    dispatch(logout())
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handlePayment = async (fee) => {
    try {
      const token = localStorage.getItem("token")
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api"

      const res = await fetch(`${API_URL}/fees/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fee_id: fee.fee_id }),
      })

      const data = await res.json()
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to create order")
      }

      const { order_id } = data.data
      if (!window.Razorpay) {
        setMessage({ text: "Payment system not available. Please try again later.", type: "error" })
        return
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        order_id: order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await fetch(`${API_URL}/fees/verify-payment`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                fee_id: fee.fee_id,
              }),
            })

            const result = await verifyResponse.json()
            if (result.success) {
              dispatch(fetchFees())
              setMessage({ text: "Payment successful!", type: "success" })
            } else {
              setMessage({ text: `Payment verification failed: ${result.message}`, type: "error" })
            }
          } catch (error) {
            setMessage({ text: `Payment verification failed: ${error.message}`, type: "error" })
          }
        },
      }

      const rzp = new window.Razorpay(options)
      rzp.open()
    } catch (err) {
      setMessage({ text: `Payment error: ${err.message}`, type: "error" })
    }
  }

  const handleViewDocument = async (doc) => {
    try {
      const { blob } = await dispatch(viewDocument(doc.doc_id)).unwrap()
      if (blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob)
        window.open(url, "_blank")
        window.URL.revokeObjectURL(url)
      } else {
        setMessage({ text: "View failed: Invalid file response received.", type: "error" })
      }
    } catch (error) {
      setMessage({ text: `View failed: ${error.message}`, type: "error" })
    }
  }

  const handleDownloadDocument = async (doc) => {
    try {
      const { blob } = await dispatch(downloadDocument(doc.doc_id)).unwrap()
      if (blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.download = doc.doc_name || `document_${doc.doc_id}.pdf`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        setMessage({ text: "Document downloaded successfully!", type: "success" })
      } else {
        setMessage({ text: "Download failed: Invalid file response received.", type: "error" })
      }
    } catch (error) {
      setMessage({ text: `Download failed: ${error.message}`, type: "error" })
    }
  }

  const handleEditDocument = (doc) => {
    setEditingDoc(doc)
    setShowEditModal(true)
  }

  const handleAddSuccess = (action = "uploaded") => {
    setMessage({ text: action === "updated" ? "Updated" : "Submitted", type: "success" })
    dispatch(fetchDocuments())
  }

  const handleViewFee = (fee) => {
    setViewingFee(fee)
  }

  const handleShowReceipt = (fee) => {
    setReceiptFee(fee) // Set receiptFee instead of viewingFee
    setShowReceipt(true)
  }

  const handlePrintReceipt = () => {
    try {
      const content = receiptRef.current
      if (!content) return
      const printWindow = window.open("", "PRINT", "height=800,width=700")
      printWindow.document.write("<html><head><title>Receipt</title>")
      printWindow.document.write(
        "<meta name='viewport' content='width=device-width, initial-scale=1'/>" +
          "<style>html,body{background:#fff;margin:0;padding:0}</style>",
      )
      printWindow.document.write("</head><body>")
      printWindow.document.write(content.outerHTML)
      printWindow.document.write("</body></html>")
      printWindow.document.close()
      printWindow.focus()
      printWindow.onload = () => {
        printWindow.print()
        printWindow.close()
      }
    } catch (e) {
      console.error("[v0] Print error:", e)
    }
  }

  const studentFees = fees.filter((fee) => fee.student_id === user?.id)
  const totalFees = studentFees.reduce((sum, fee) => sum + Number.parseFloat(fee.amount), 0)
  const paidFees = studentFees
    .filter((fee) => fee.status === "Paid")
    .reduce((sum, fee) => sum + Number.parseFloat(fee.amount), 0)
  const pendingFees = totalFees - paidFees
  const studentDocuments = documents.filter((doc) => doc.student_id === user?.id)
  const studentResults = results.filter((result) => result.student_id === user?.id)

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return "₹0"
    const numericAmount = typeof amount === "string" ? Number.parseFloat(amount.replace(/[^0-9.-]+/g, "")) : amount
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(
      numericAmount,
    )
  }

  const menuItems = [
    { id: "profile", label: "My Profile", icon: User },
    { id: "fees", label: "Fee Payment", icon: CreditCard },
    { id: "results", label: "Results", icon: FileText },
    { id: "documents", label: "Documents", icon: FolderOpen },
    { id: "idcard", label: "ID Card", icon: IdCard },
  ]

  const renderProfileSection = () => (
    <div className="flex flex-col gap-8">
      <div>
        <h1 className="text-3xl font-extrabold text-gray-900 mb-1">Student Dashboard</h1>
        <p className="text-gray-600 font-medium">
          Welcome back, <span className="text-gray-900 font-bold">{currentStudent?.name || "Student"}</span>. Manage
          your profile, fees, and documents.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            label: "Total Fees",
            value: formatCurrency(totalFees),
            icon: <IndianRupee size={32} className="text-white" />,
            color: PRIMARY_COLOR,
          },
          {
            label: "Paid Fees",
            value: formatCurrency(paidFees),
            icon: <CheckCircle size={32} className="text-white" />,
            color: ACCENT_COLOR,
          },
          {
            label: "Pending Fees",
            value: formatCurrency(pendingFees),
            icon: <Clock size={32} className="text-white" />,
            color: WARNING_COLOR,
          },
          {
            label: "Documents Uploaded",
            value: studentDocuments.length,
            icon: <FolderOpen size={32} className="text-white" />,
            color: PRIMARY_COLOR,
          },
        ].map((card, idx) => (
          <div
            key={idx}
            className="bg-white p-6 rounded-xl shadow-lg border-t-4 transition duration-300 transform hover:shadow-xl hover:scale-[1.01]"
            style={{ borderColor: card.color }}
          >
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{card.label}</p>
                <p className="text-3xl font-extrabold text-gray-900 mt-1">{card.value}</p>
              </div>
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center shadow-md"
                style={{ backgroundColor: card.color }}
              >
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-lg border-t-4" style={{ borderColor: PRIMARY_COLOR }}>
          <div className="flex items-center justify-between mb-4">
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
              Profile Information
            </Typography>
            <GraduationCap size={40} color={PRIMARY_COLOR} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>
                Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "medium", color: "#333" }}>
                {currentStudent?.name || "N/A"}
              </Typography>
            </div>
            <div>
              <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>
                Student ID
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "medium", color: "#333" }}>
                {currentStudent?.student_code || currentStudent?.student_id || "N/A"}
              </Typography>
            </div>
            <div>
              <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>
                Email
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "medium", color: "#333" }}>
                {currentStudent?.email || "N/A"}
              </Typography>
            </div>
            <div>
              <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>
                Mobile
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "medium", color: "#333" }}>
                {currentStudent?.mobile_no || "N/A"}
              </Typography>
            </div>
            <div>
              <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>
                Date of Birth
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "medium", color: "#333" }}>
                {currentStudent?.dob ? new Date(currentStudent.dob).toLocaleDateString() : "N/A"}
              </Typography>
            </div>
            <div>
              <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>
                Trade
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "medium", color: "#333" }}>
                {currentStudent?.trade || "N/A"}
              </Typography>
            </div>
            <div>
              <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>
                Institute
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "medium", color: "#333" }}>
                {currentStudent?.department?.name || "N/A"}
              </Typography>
            </div>
            <div>
              <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>
                Admission Status
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "medium",
                  color:
                    currentStudent?.admission_status === "Enrolled"
                      ? ACCENT_COLOR
                      : currentStudent?.admission_status === "Rejected"
                      ? DANGER_COLOR
                      : WARNING_COLOR,
                }}
              >
                {currentStudent?.admission_status || "Pending Review"}
              </Typography>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-lg border-t-4" style={{ borderColor: ACCENT_COLOR }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333", mb: 2 }}>
            Family Details
          </Typography>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>
                Father's Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "medium", color: "#333" }}>
                {currentStudent?.fathers_name || "N/A"}
              </Typography>
            </div>
            <div>
              <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>
                Mother's Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "medium", color: "#333" }}>
                {currentStudent?.mothers_name || "N/A"}
              </Typography>
            </div>
            <div>
              <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>
                Location
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "medium", color: "#333" }}>
                {currentStudent?.district || "N/A"}, {currentStudent?.state || "N/A"}
              </Typography>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  )

  const renderFeesSection = () => (
    <div className="flex flex-col gap-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          {
            label: "Total Fees",
            value: formatCurrency(totalFees),
            icon: <IndianRupee size={32} className="text-white" />,
            color: PRIMARY_COLOR,
          },
          {
            label: "Paid Fees",
            value: formatCurrency(paidFees),
            icon: <CheckCircle size={32} className="text-white" />,
            color: ACCENT_COLOR,
          },
          {
            label: "Pending Fees",
            value: formatCurrency(pendingFees),
            icon: <Clock size={32} className="text-white" />,
            color: WARNING_COLOR,
          },
        ].map((card, idx) => (
          <div
            key={idx}
            className="bg-white p-6 rounded-xl shadow-lg border-t-4 transition duration-300 transform hover:shadow-xl hover:scale-[1.01]"
            style={{ borderColor: card.color }}
          >
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{card.label}</p>
                <p className="text-3xl font-extrabold text-gray-900 mt-1">{card.value}</p>
              </div>
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center shadow-md"
                style={{ backgroundColor: card.color }}
              >
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white p-6 rounded-xl shadow-lg border-t-4" style={{ borderColor: PRIMARY_COLOR }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333", mb: 2 }}>
          Fee Details
        </Typography>
        {studentFees.length === 0 ? (
          <div className="text-center py-6">
            <IndianRupee size={48} color="#ccc" />
            <Typography variant="body1" sx={{ color: "#666", mt: 2 }}>
              No fee records available yet
            </Typography>
          </div>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ borderBottom: "1px solid #ccc" }}>
                  <TableCell sx={{ fontWeight: "bold", color: "#555" }}>FEE ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#555" }}>AMOUNT</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#555" }}>STATUS</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#555" }}>PAYMENT DATE</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#555" }}>PAYMENT ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#555" }}>ACTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {studentFees.map((fee) => (
                  <TableRow key={fee.fee_id} sx={{ borderBottom: "1px solid #ccc" }}>
                    <TableCell sx={{ color: "#333" }}>{fee.fee_id}</TableCell>
                    <TableCell sx={{ color: "#333" }}>{formatCurrency(fee.amount)}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: fee.status === "Paid" ? "#e8f5e9" : "#fff3e0",
                          color: fee.status === "Paid" ? ACCENT_COLOR : WARNING_COLOR,
                          display: "inline-block",
                        }}
                      >
                        {fee.status}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#555" }}>
                      {fee.payment_date ? new Date(fee.payment_date).toLocaleDateString() : "-"}
                    </TableCell>
                    <TableCell sx={{ color: "#333" }}>{fee.razorpay_payment_id || "-"}</TableCell>
                    <TableCell>
                      {fee.status === "Pending" ? (
                        <Button
                          variant="contained"
                          onClick={() => handlePayment(fee)}
                          sx={{
                            textTransform: "none",
                            bgcolor: PRIMARY_COLOR,
                            color: "#fff",
                            "&:hover": { bgcolor: PRIMARY_LIGHT },
                            px: 2,
                            py: 0.5,
                          }}
                        >
                          Pay Now
                        </Button>
                      ) : (
                        <div className="flex gap-1">
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => handleViewFee(fee)}
                              sx={{ bgcolor: PRIMARY_COLOR, color: "#fff", "&:hover": { bgcolor: PRIMARY_LIGHT } }}
                            >
                              <Eye size={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Receipt">
                            <IconButton
                              size="small"
                              onClick={() => handleShowReceipt(fee)}
                              sx={{ bgcolor: "#10b981", color: "#fff", "&:hover": { bgcolor: "#059669" } }}
                            >
                              <ReceiptIcon size={16} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </div>
    </div>
  )

  const renderResultsSection = () => (
    <div className="flex flex-col gap-8">
      {resultsError && (
        <div className="p-4 rounded-xl bg-red-50 border-t-4 border-red-500">
          <Typography variant="body2" sx={{ color: DANGER_COLOR }}>
            {resultsError}
          </Typography>
        </div>
      )}
      <div className="bg-white p-6 rounded-xl shadow-lg border-t-4" style={{ borderColor: PRIMARY_COLOR }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333", mb: 2 }}>
          Academic Results
        </Typography>
        {resultsLoading ? (
          <div className="text-center py-6">
            <CircularProgress sx={{ color: PRIMARY_COLOR }} />
            <Typography variant="body1" sx={{ color: "#666", mt: 2 }}>
              Loading results...
            </Typography>
          </div>
        ) : studentResults.length === 0 ? (
          <div className="text-center py-6">
            <FileText size={64} color="#ccc" />
            <Typography variant="body1" sx={{ color: "#666", mt: 2 }}>
              No results available yet
            </Typography>
          </div>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ borderBottom: "1px solid #ccc" }}>
                  <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Result ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Subject</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Marks</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#555" }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {studentResults.map((result) => (
                  <TableRow key={result.result_id} sx={{ borderBottom: "1px solid #ccc" }}>
                    <TableCell sx={{ color: "#333" }}>{result.result_id}</TableCell>
                    <TableCell sx={{ color: "#333" }}>{result.exam_name || "N/A"}</TableCell>
                    <TableCell sx={{ color: "#333" }}>{result.marks || "N/A"}</TableCell>
                    <TableCell sx={{ color: "#333" }}>{result.status || "N/A"}</TableCell>
                    <TableCell sx={{ color: "#555" }}>
                      {result.declared_at ? new Date(result.declared_at).toLocaleDateString() : "N/A"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </div>
    </div>
  )

  const renderDocumentsSection = () => (
    <div className="flex flex-col gap-8">
      {message.text && (
        <div
          className={`p-4 rounded-xl ${
            message.type === "success"
              ? "bg-green-50 border-t-4 border-green-500"
              : "bg-red-50 border-t-4 border-red-500"
          }`}
        >
          <Typography variant="body2" sx={{ color: message.type === "success" ? ACCENT_COLOR : DANGER_COLOR }}>
            {message.text}
          </Typography>
        </div>
      )}
      <div className="bg-white p-6 rounded-xl shadow-lg border-t-4" style={{ borderColor: PRIMARY_COLOR }}>
        <div className="flex items-center justify-between mb-2">
          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
            My Documents
          </Typography>
          <Button
            onClick={() => setShowAddModal(true)}
            startIcon={<Upload size={16} />}
            variant="contained"
            sx={{
              textTransform: "none",
              bgcolor: PRIMARY_COLOR,
              color: "#fff",
              "&:hover": { bgcolor: PRIMARY_LIGHT },
              px: 2,
              py: 0.5,
            }}
          >
            Upload New Document
          </Button>
        </div>
        {studentDocuments.length === 0 ? (
          <div className="text-center py-6">
            <FolderOpen size={64} color="#ccc" />
            <Typography variant="body1" sx={{ color: "#666", mt: 2 }}>
              No documents available
            </Typography>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {studentDocuments.map((doc) => (
              <div key={doc.doc_id} className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center">
                  <div>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#333" }}>
                      {doc.doc_name || doc.document_type}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", mt: 0.5 }}>
                      Uploaded: {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : "N/A"}
                    </Typography>
                    {doc.document_type && (
                      <Typography variant="caption" sx={{ color: "#999" }}>
                        Type: {doc.document_type}
                      </Typography>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDocument(doc)}
                        sx={{
                          bgcolor: PRIMARY_COLOR,
                          color: "#fff",
                          "&:hover": { bgcolor: PRIMARY_LIGHT },
                        }}
                      >
                        <Eye size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadDocument(doc)}
                        sx={{
                          bgcolor: ACCENT_COLOR,
                          color: "#fff",
                          "&:hover": { bgcolor: "#00a843" },
                        }}
                      >
                        <Download size={16} />
                      </IconButton>
                    </Tooltip>
                  
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  const renderIdCardSection = () => (
    <div className="flex items-center justify-center">
      <div className="bg-white p-6 rounded-2xl shadow-xl">
        <StudentIDCard student={currentStudent} />
      </div>
    </div>
  )

  const renderContent = () => {
    if (authLoading) {
      return (
        <div className="min-h-[400px] flex items-center justify-center">
          <CircularProgress sx={{ color: PRIMARY_COLOR }} />
        </div>
      )
    }

    switch (activeSection) {
      case "profile":
        return renderProfileSection()
      case "fees":
        return renderFeesSection()
      case "results":
        return renderResultsSection()
      case "documents":
        return renderDocumentsSection()
      case "idcard":
        return renderIdCardSection()
      default:
        return renderProfileSection()
    }
  }

  const drawerContent = (
    <div className="p-4 h-full flex flex-col justify-between" style={{ backgroundColor: "#ffffff" }}>
      <List>
        {menuItems.map((item) => {
          const Icon = item.icon
          const isSelected = activeSection === item.id
          return (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                selected={isSelected}
                onClick={() => {
                  setActiveSection(item.id)
                  if (isMobile) setMobileOpen(false)
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  "&.Mui-selected": {
                    bgcolor: PRIMARY_COLOR,
                    color: "#fff",
                    boxShadow: "0 4px 6px rgba(63, 81, 181, 0.3)",
                    "&:hover": { bgcolor: PRIMARY_LIGHT },
                  },
                  "&:hover": { bgcolor: BG_COLOR },
                  transition: "all 0.2s",
                }}
              >
                <ListItemIcon>
                  <Icon size={20} color={isSelected ? "#fff" : PRIMARY_COLOR} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? "bold" : "medium",
                    color: isSelected ? "#fff" : "#333",
                  }}
                />
              </ListItemButton>
            </ListItem>
          )
        })}
      </List>
      <Box sx={{ p: 2, borderTop: "1px solid #eee" }}>
        <Button
          fullWidth
          onClick={handleLogout}
          startIcon={<LogOut size={16} />}
          variant="contained"
          sx={{
            textTransform: "none",
            bgcolor: DANGER_COLOR,
            "&:hover": { bgcolor: "#b71c1c" },
            borderRadius: 2,
          }}
        >
          Logout
        </Button>
      </Box>
    </div>
  )

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: BG_COLOR }}>
        <div className="text-center">
          <CircularProgress sx={{ color: PRIMARY_COLOR, mb: 2 }} />
          <Typography variant="body1" sx={{ color: "#666" }}>
            Loading Student Dashboard...
          </Typography>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: BG_COLOR }}>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: "#fff",
          borderBottom: "none",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ px: 3, py: 1 }}>
          <div className="flex items-center gap-2 flex-grow">
            {isMobile && (
              <IconButton edge="start" onClick={handleDrawerToggle} sx={{ color: PRIMARY_COLOR }}>
                <MenuIcon />
              </IconButton>
            )}
            <GraduationCap size={28} color={PRIMARY_COLOR} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#333" }}>
              STUDENT PORTAL
            </Typography>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <Typography variant="body1" sx={{ fontWeight: "bold", color: "#333" }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: PRIMARY_COLOR, fontWeight: "medium" }}>
                Student
              </Typography>
            </div>
            {!isMobile && (
              <Button
                onClick={handleLogout}
                startIcon={<LogOut size={18} />}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  borderColor: DANGER_COLOR,
                  color: DANGER_COLOR,
                  "&:hover": {
                    bgcolor: DANGER_COLOR,
                    color: "#fff",
                    borderColor: DANGER_COLOR,
                  },
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                }}
              >
                Logout
              </Button>
            )}
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: 240,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: 240,
            boxSizing: "border-box",
            borderRight: "none",
            boxShadow: "2px 0 5px rgba(0,0,0,0.05)",
            bgcolor: "#fff",
            top: 64,
            height: "calc(100% - 64px)",
          },
        }}
      >
        {drawerContent}
      </Drawer>
      <main className="flex-grow p-8 transition-all duration-300" style={{ marginTop: 64 }}>
        {renderContent()}
      </main>
      {showAddModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 2000 }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">Upload New Document</h3>
            <AddDocument setShowAddModal={setShowAddModal} onSubmitSuccess={(action) => handleAddSuccess(action)} />
          </div>
        </div>
      )}
      {showEditModal && editingDoc && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 2000 }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-3">
              Edit Document (ID: {editingDoc.doc_id})
            </h3>
            <AddDocument
              setShowAddModal={setShowEditModal}
              editingDoc={editingDoc}
              onSubmitSuccess={(action) => handleAddSuccess(action)}
            />
          </div>
        </div>
      )}
      {viewingFee && !showReceipt && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 2000 }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
                Fee Details (ID: {viewingFee.fee_id})
              </Typography>
              <Button onClick={() => setViewingFee(null)} variant="text">
                Close
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <b>Amount:</b>{" "}
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                  minimumFractionDigits: 0,
                }).format(Number(viewingFee.amount || 0))}
              </div>
              <div>
                <b>Status:</b> {viewingFee.status}
              </div>
              <div>
                <b>Payment Date:</b>{" "}
                {viewingFee.payment_date ? new Date(viewingFee.payment_date).toLocaleString() : "-"}
              </div>
              <div>
                <b>Payment ID:</b> {viewingFee.razorpay_payment_id || "-"}
              </div>
              <div>
                <b>Transaction ID:</b> {viewingFee.transaction_id || "-"}
              </div>
            </div>
          </div>
        </div>
      )}
      {showReceipt && receiptFee && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 2100 }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
                Payment Receipt
              </Typography>
              <div className="flex gap-2">
                <Button
                  onClick={handlePrintReceipt}
                  startIcon={<Printer size={16} />}
                  variant="contained"
                  sx={{ textTransform: "none", bgcolor: PRIMARY_COLOR, "&:hover": { bgcolor: PRIMARY_LIGHT } }}
                >
                  Print
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setShowReceipt(false)
                    setReceiptFee(null)
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
            <div className="overflow-auto">
              <div ref={receiptRef}>
                <StudentFeeReceipt
                  student={currentStudent}
                  fee={receiptFee}
                  institute={{
                    name: currentStudent?.department?.name,
                    code: currentStudent?.department_code,
                    address:
                      currentStudent?.department_address ||
                      [currentStudent?.district, currentStudent?.state].filter(Boolean).join(", "),
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StudentDashboard